import { useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  VisibilityState,
  Updater,
  functionalUpdate,
  ColumnPinningState,
} from '@tanstack/react-table';
import {
  PreferenceControllerGetPreferencesParamsTypeEnum,
  PreferenceControllerUpsertPreferencesParamsTypeEnum,
  PreferenceDtoTypeEnum,
  PreferenceListDto,
  QueryKey,
  SpendBrainTableName,
  TableColumnPreference,
  TableSettings,
  preferenceKeys,
  useGetPreferences,
  useUpdatePreferences,
} from '@sib/api';
import { useUserSetupStore } from '@sib/auth';

const toVisibilityState = (
  prefs: Record<string, TableColumnPreference>
): VisibilityState =>
  Object.fromEntries(
    Object.entries(prefs).map(([key, { visible }]) => [key, visible ?? true])
  );

const toPinningState = (
  prefs: Record<string, TableColumnPreference>
): ColumnPinningState => {
  const state: ColumnPinningState = { left: [], right: [] };
  Object.entries(prefs).forEach(([columnId, { pinned }]) => {
    if (pinned === 'left') state.left?.push(columnId);
    if (pinned === 'right') state.right?.push(columnId);
  });

  return state;
};

const mergePinningStates = (
  ...states: ColumnPinningState[]
): ColumnPinningState => {
  const left = new Set<string>();
  const right = new Set<string>();

  states.forEach(({ left: l = [], right: r = [] }) => {
    l.forEach(col => {
      left.add(col);
      right.delete(col);
    });
    r.forEach(col => {
      right.add(col);
      left.delete(col);
    });
  });

  return {
    left: Array.from(left),
    right: Array.from(right),
  };
};

const applyUserUnpinOverrides = (
  state: ColumnPinningState,
  userPrefs: Record<string, TableColumnPreference>
): ColumnPinningState => {
  const unpinnedByUser = new Set(
    Object.entries(userPrefs)
      .filter(([, pref]) => pref.pinned === false)
      .map(([col]) => col)
  );
  if (unpinnedByUser.size === 0) return state;

  return {
    left: (state.left ?? []).filter(col => !unpinnedByUser.has(col)),
    right: (state.right ?? []).filter(col => !unpinnedByUser.has(col)),
  };
};

const clearPinning = (
  prefs: Record<string, TableColumnPreference>
): Record<string, TableColumnPreference> =>
  Object.fromEntries(
    Object.entries(prefs).map(([key, pref]) => [
      key,
      pref.pinned ? { ...pref, pinned: false } : pref,
    ])
  );

const USER_PREFERENCES_QUERY_KEY = [QueryKey.Resource.UserPreferences];

export const useTablePreferencesSettings = (tableName: SpendBrainTableName) => {
  const organizationId = useUserSetupStore(state => state.organization.id);
  const queryClient = useQueryClient();
  const { mutate } = useUpdatePreferences();

  const { data: orgData } = useGetPreferences({
    key: preferenceKeys.tableSettings,
    type: PreferenceControllerGetPreferencesParamsTypeEnum.Organization,
    organizationId,
  });

  const { data } = useGetPreferences({
    key: preferenceKeys.tableSettings,
  });

  const hasLoaded = Boolean(data && orgData);

  const getTableSettings = (
    type: PreferenceDtoTypeEnum,
    source = data
  ): TableSettings =>
    (source?.items.find(item => item.type === type)?.value as TableSettings) ??
    {};

  const globalSettings = getTableSettings(PreferenceDtoTypeEnum.Global);
  const orgSettings = getTableSettings(
    PreferenceDtoTypeEnum.Organization,
    orgData
  );
  const userSettings = getTableSettings(PreferenceDtoTypeEnum.User);

  const globalPrefs = useMemo(
    () => globalSettings[tableName] ?? {},
    [globalSettings, tableName]
  );
  const orgPrefs = useMemo(
    () => orgSettings[tableName] ?? {},
    [orgSettings, tableName]
  );
  const userPrefs = useMemo(
    () => userSettings[tableName] ?? {},
    [userSettings, tableName]
  );

  const setPreferencesCacheOptimistically = useCallback(
    (newUserSettings: TableSettings) => {
      queryClient.setQueryData<PreferenceListDto>(
        USER_PREFERENCES_QUERY_KEY,
        (old): PreferenceListDto | undefined => {
          if (!old) return old;

          return {
            ...old,
            items: old.items.map(item =>
              item.type === PreferenceDtoTypeEnum.User
                ? { ...item, value: newUserSettings }
                : item
            ),
          };
        }
      );
    },
    [queryClient]
  );

  const disabledColumns = useMemo(
    () =>
      new Set(
        Object.entries(globalPrefs)
          .filter(
            ([key, pref]) =>
              pref.enabled === false && orgPrefs[key]?.enabled !== true
          )
          .map(([key]) => key)
      ),
    [globalPrefs, orgPrefs]
  );

  const columnVisibility = useMemo<VisibilityState | null>(() => {
    if (!hasLoaded) return null;

    return {
      ...toVisibilityState(globalPrefs),
      ...toVisibilityState(orgPrefs),
      ...toVisibilityState(userPrefs),
      ...Object.fromEntries(
        Array.from(disabledColumns).map(col => [col, false])
      ),
    };
  }, [hasLoaded, globalPrefs, orgPrefs, userPrefs, disabledColumns]);

  const saveVisibility = useCallback(
    (next: VisibilityState) => {
      if (!hasLoaded) return;

      const filtered = Object.fromEntries(
        Object.entries(next).filter(([key]) => !disabledColumns.has(key))
      );

      const updatedTablePrefs = Object.fromEntries(
        Object.entries(filtered).map(([key, visible]) => [
          key,
          { ...(userPrefs[key] ?? {}), visible },
        ])
      );

      const newUserSettings = {
        ...userSettings,
        [tableName]: {
          ...userPrefs,
          ...updatedTablePrefs,
        },
      };

      setPreferencesCacheOptimistically(newUserSettings);

      mutate({
        key: preferenceKeys.tableSettings,
        query: {
          key: preferenceKeys.tableSettings,
          type: PreferenceControllerUpsertPreferencesParamsTypeEnum.User,
        },
        data: {
          value: newUserSettings,
        },
      });
    },
    [
      hasLoaded,
      disabledColumns,
      mutate,
      tableName,
      userPrefs,
      userSettings,
      setPreferencesCacheOptimistically,
    ]
  );

  const onColumnVisibilityChange = useCallback(
    (updater: Updater<VisibilityState>) => {
      const next = functionalUpdate(updater, columnVisibility ?? {});
      saveVisibility(next);
    },
    [columnVisibility, saveVisibility]
  );

  const columnPinning = useMemo<ColumnPinningState | null>(() => {
    if (!hasLoaded) return null;

    const merged = mergePinningStates(
      toPinningState(globalPrefs),
      toPinningState(orgPrefs),
      toPinningState(userPrefs)
    );

    return applyUserUnpinOverrides(merged, userPrefs);
  }, [hasLoaded, globalPrefs, orgPrefs, userPrefs]);

  const savePinning = useCallback(
    (next: ColumnPinningState) => {
      if (!hasLoaded) return;

      const nextPinned = new Set([...(next.left ?? []), ...(next.right ?? [])]);
      const cleared = clearPinning(userPrefs);
      const updated: Record<string, TableColumnPreference> = { ...cleared };

      next.left?.forEach(col => {
        updated[col] = { ...(updated[col] ?? {}), pinned: 'left' };
      });

      next.right?.forEach(col => {
        updated[col] = { ...(updated[col] ?? {}), pinned: 'right' };
      });

      const previouslyPinned = [
        ...(columnPinning?.left ?? []),
        ...(columnPinning?.right ?? []),
      ];
      previouslyPinned.forEach(col => {
        if (!nextPinned.has(col)) {
          updated[col] = { ...(updated[col] ?? {}), pinned: false };
        }
      });

      const newUserSettings = {
        ...userSettings,
        [tableName]: updated,
      };

      setPreferencesCacheOptimistically(newUserSettings);

      mutate({
        key: preferenceKeys.tableSettings,
        query: {
          key: preferenceKeys.tableSettings,
          type: PreferenceControllerUpsertPreferencesParamsTypeEnum.User,
        },
        data: {
          value: newUserSettings,
        },
      });
    },
    [
      hasLoaded,
      mutate,
      tableName,
      userPrefs,
      userSettings,
      columnPinning,
      setPreferencesCacheOptimistically,
    ]
  );

  const onColumnPinningChange = useCallback(
    (updater: Updater<ColumnPinningState>) => {
      const next = functionalUpdate(updater, columnPinning ?? {});
      savePinning(next);
    },
    [columnPinning, savePinning]
  );

  return {
    disabledColumns,
    onColumnVisibilityChange,
    onColumnPinningChange,
    state: {
      columnVisibility,
      columnPinning,
    },
  };
};
